import React from 'react'
import { graphql, useFragment, usePaginationFragment } from 'react-relay'
import { Trans } from '@lingui/react/macro'

import { YStack, XStack, Separator, useMedia } from 'tamagui'

import { UserPrimaryPhoto } from '@newComponents/User/UserPrimaryPhoto'
import { Link } from '@newComponents/Link'
import { Grid } from '@newComponents/Grid'
import { AutoLoadMore } from '@newComponents/AutoLoadMore'
import { Card } from '@newComponents/Card'
import { H2, H3 } from '@newComponents/Text'

import { INITIAL_COUNTS } from './GuildMembersScreen'

import { GuildMembersNonOrganizersSection_guild$key } from './__generated__/GuildMembersNonOrganizersSection_guild.graphql'
import { GuildMembersNonOrganizersSection_member$key } from './__generated__/GuildMembersNonOrganizersSection_member.graphql'

function useItemsPerRow() {
  const media = useMedia()
  if (media.xs) return INITIAL_COUNTS[0]
  if (media.sm) return INITIAL_COUNTS[1]
  if (media.md) return INITIAL_COUNTS[2]
  if (media.lg) return INITIAL_COUNTS[3]
  return INITIAL_COUNTS[4]
}

const MemberCard = ({
  user,
  expand = false,
}: {
  user: GuildMembersNonOrganizersSection_member$key
  expand?: boolean
}) => {
  const data = useFragment(
    graphql`
      fragment GuildMembersNonOrganizersSection_member on User {
        slugId
        firstName
        lastName
        ...UserPrimaryPhotoNew_user
      }
    `,
    user
  )

  return (
    <Link
      display='flex'
      flex={expand ? 1 : undefined}
      href={`/${data.slugId}`}
      hoverStyle={{ textDecorationLine: 'none' }}
      color='$colorStrong'
      flexDirection='column'
      size='$4'
    >
      <Card
        glass
        padding='$4'
        flex={expand ? 1 : undefined}
        flexDirection='row'
        alignItems='center'
        gap='$4'
      >
        <UserPrimaryPhoto user={data} size='$12' />
        <H3 size='$5' numberOfLines={1}>
          {data.firstName} {data.lastName}
        </H3>
      </Card>
    </Link>
  )
}

export const GuildMembersNonOrganizersSection = ({
  guild,
}: {
  guild: GuildMembersNonOrganizersSection_guild$key
}) => {
  const { data, hasNext, loadNext, isLoadingNext } = usePaginationFragment(
    graphql`
      fragment GuildMembersNonOrganizersSection_guild on Guild
      @refetchable(queryName: "GuildMembersNonOrganizersSectionPaginationQuery")
      @argumentDefinitions(
        count: { type: "Int", defaultValue: 12 }
        cursor: { type: "Cursor" }
      ) {
        members(first: $count, after: $cursor)
          @connection(key: "GuildMembersNonOrganizersSection_guild_members") {
          edges {
            node {
              ...GuildMembersNonOrganizersSection_member
            }

            cursor
          }
        }
      }
    `,
    guild
  )

  const itemsPerRow = useItemsPerRow()
  const media = useMedia()

  if (data.members.edges.length === 0) {
    return null
  }

  if (media.xs) {
    return (
      <YStack gap='$4'>
        <XStack gap='$4' alignItems='center'>
          <H2 size='$4' color='$colorMuted' fontWeight='$5'>
            <Trans>Members</Trans>
          </H2>
          <Separator flex={1} borderColor='$borderColor' />
        </XStack>

        {data.members.edges.map((edge) => (
          <MemberCard key={edge.cursor} user={edge.node} />
        ))}

        <AutoLoadMore
          hasNext={hasNext}
          loadNext={loadNext}
          isLoadingNext={isLoadingNext}
          count={12}
        />
      </YStack>
    )
  }

  const memberGroups = data.members.edges.reduce<
    Array<Array<(typeof data.members.edges)[number]>>
  >((memo, member, index) => {
    if (index % itemsPerRow === 0) {
      memo.push([member])
    } else {
      memo[memo.length - 1].push(member)
    }

    return memo
  }, [])

  return (
    <YStack gap='$4' paddingBottom='$4'>
      <XStack gap='$4' alignItems='center'>
        <H2 size='$4' color='$colorMuted' fontWeight='$5'>
          <Trans>Members</Trans>
        </H2>
        <Separator flex={1} borderColor='$borderColor' />
      </XStack>

      <YStack gap='$4'>
        {memberGroups.map((group, index) => {
          return (
            <XStack key={index}>
              <Grid gap='$4'>
                {group.map((edge) => (
                  <Grid.Column key={edge.cursor}>
                    <MemberCard user={edge.node} expand />
                  </Grid.Column>
                ))}

                {Array.from({
                  length: itemsPerRow - group.length,
                }).map((_, i) => (
                  <Grid.Column key={i} />
                ))}
              </Grid>
            </XStack>
          )
        })}
      </YStack>

      <AutoLoadMore
        hasNext={hasNext}
        loadNext={loadNext}
        isLoadingNext={isLoadingNext}
        count={12}
      />
    </YStack>
  )
}
